/**
 * Les profils de sources du mode `?demo` (ARTO-130) : ce que le profilage
 * dirait de quelques exports « sales » — dates ambiguës, montants à l'ancienne,
 * colonne sans nom, classeur illisible. Des intitulés inventés, aucune
 * galerie ni personne réelle. On choisit la fiche d'après le nom du fichier.
 */
import type {
  ColonneProfilee,
  FichierProfile,
  FormatColonne,
  SuggestionProfil,
  TableProfilee,
} from './correspondance.js';
import type { LigneEchantillon } from './demoMoteur.js';
import {
  ID_PROFIL_MAILCHIMP_ABONNES,
  ID_PROFIL_MAILCHIMP_DESABONNES,
} from './demoProfilsEditeurs.js';

interface DefColonne {
  nom: string;
  remplissage: number;
  distinctes: number;
  format: FormatColonne;
  frequentes: [string, number][];
  decimal?: ',' | '.';
}

function colonne(def: DefColonne): ColonneProfilee {
  return {
    nom: def.nom,
    remplissage: def.remplissage,
    distinctes: def.distinctes,
    frequentes: def.frequentes.map(([valeur, nombre]) => ({ valeur, nombre })),
    format: def.format,
    conformite: def.format === 'texte' || def.format === 'vide' ? 1 : 0.99,
    ...(def.decimal ? { decimal: def.decimal } : {}),
  };
}

function table(
  nom: string | null,
  lignes: number,
  defs: DefColonne[],
  echantillon: Record<string, string>[],
): TableProfilee {
  return {
    nom,
    lignes,
    entetes: defs.map((def) => def.nom),
    colonnes: defs.map(colonne),
    echantillon: echantillon.map((valeurs, rang) => ({ ligne: rang + 2, valeurs })),
  };
}

// ── Œuvres : un classeur à nettoyer ──────────────────────────────────────

const OEUVRES = table(
  'Inventaire',
  8412,
  [
    {
      nom: 'Réf',
      remplissage: 1,
      distinctes: 8412,
      format: 'texte',
      frequentes: [
        ['A-0001', 1],
        ['A-0002', 1],
      ],
    },
    {
      nom: 'Titre',
      remplissage: 0.99,
      distinctes: 7980,
      format: 'texte',
      frequentes: [
        ['Sans titre', 214],
        ['Nature morte', 61],
      ],
    },
    {
      nom: 'Prénom artiste',
      remplissage: 0.94,
      distinctes: 311,
      format: 'texte',
      frequentes: [
        ['marie', 402],
        ['PAUL', 233],
        ['Inès', 190],
      ],
    },
    {
      nom: 'Nom artiste',
      remplissage: 1,
      distinctes: 402,
      format: 'texte',
      frequentes: [
        ['DELORME', 402],
        ['kowalczyk', 190],
        ['Vasseur', 233],
      ],
    },
    {
      nom: 'Technique',
      remplissage: 0.92,
      distinctes: 48,
      format: 'texte',
      frequentes: [
        ['Huile sur toile', 2104],
        ['Acrylique', 1310],
        ['Gravure', 640],
      ],
    },
    {
      nom: 'Dimensions',
      remplissage: 0.88,
      distinctes: 1203,
      format: 'texte',
      frequentes: [
        ['50 x 70 cm', 301],
        ['100 x 81 cm', 244],
      ],
    },
    {
      nom: 'Prix TTC',
      remplissage: 0.81,
      distinctes: 1450,
      format: 'montant',
      decimal: ',',
      frequentes: [
        ['1 200,00 €', 112],
        ['850,00 €', 97],
      ],
    },
    {
      nom: 'Statut',
      remplissage: 1,
      distinctes: 4,
      format: 'texte',
      frequentes: [
        ['En stock', 5120],
        ['Vendu', 2480],
        ['Prêté', 420],
        ['archivé', 392],
      ],
    },
    {
      nom: 'Emplacement',
      remplissage: 0.76,
      distinctes: 14,
      format: 'texte',
      frequentes: [
        ['Réserve A', 2210],
        ['Galerie', 1980],
      ],
    },
    { nom: 'Colonne 11', remplissage: 0, distinctes: 0, format: 'vide', frequentes: [] },
  ],
  [
    {
      Réf: 'A-0001',
      Titre: 'Étude pour un paysage',
      'Prénom artiste': 'marie',
      'Nom artiste': 'DELORME',
      Technique: 'Huile sur toile',
      Dimensions: '50 x 70 cm',
      'Prix TTC': '1 200,00 €',
      Statut: 'En stock',
      Emplacement: 'Réserve A',
      'Colonne 11': '',
    },
    {
      Réf: 'A-0002',
      Titre: 'Nature morte aux coings',
      'Prénom artiste': 'PAUL',
      'Nom artiste': 'vasseur',
      Technique: 'Acrylique',
      Dimensions: '100 x 81 cm',
      'Prix TTC': '850,00 €',
      Statut: 'Vendu',
      Emplacement: 'Galerie',
      'Colonne 11': '',
    },
    {
      Réf: 'A-0003',
      Titre: 'Sans titre (série bleue)',
      'Prénom artiste': 'Inès',
      'Nom artiste': 'KOWALCZYK',
      Technique: 'Gravure',
      Dimensions: '30 x 40 cm',
      'Prix TTC': '2 450,50 €',
      Statut: 'Prêté',
      Emplacement: '',
      'Colonne 11': '',
    },
    {
      Réf: 'A-0004',
      Titre: 'Fenêtre sur cour',
      'Prénom artiste': '',
      'Nom artiste': 'DELORME',
      Technique: 'Huile sur toile',
      Dimensions: '65 x 54 cm',
      'Prix TTC': '',
      Statut: 'archivé',
      Emplacement: 'Réserve A',
      'Colonne 11': '',
    },
    {
      Réf: 'A-0005',
      Titre: 'Hiver blanc',
      'Prénom artiste': 'marie',
      'Nom artiste': 'DELORME',
      Technique: 'Acrylique',
      Dimensions: '92 x 73 cm',
      'Prix TTC': '3 100,00 €',
      Statut: 'En stock',
      Emplacement: 'Galerie',
      'Colonne 11': '',
    },
    {
      Réf: 'A-0006',
      Titre: '',
      'Prénom artiste': 'PAUL',
      'Nom artiste': 'vasseur',
      Technique: 'Gravure',
      Dimensions: '24 x 18 cm',
      'Prix TTC': '320,00 €',
      Statut: 'En stock',
      Emplacement: 'Réserve A',
      'Colonne 11': '',
    },
  ],
);

// ── Prix : un second fichier, à joindre par la référence ─────────────────

const PRIX = table(
  null,
  8380,
  [
    { nom: 'Réf', remplissage: 1, distinctes: 8380, format: 'texte', frequentes: [['A-0001', 1]] },
    {
      nom: 'Prix de vente',
      remplissage: 0.97,
      distinctes: 1390,
      format: 'montant',
      decimal: ',',
      frequentes: [
        ['1200,00', 112],
        ['850,00', 97],
      ],
    },
    { nom: 'Devise', remplissage: 1, distinctes: 1, format: 'devise', frequentes: [['EUR', 8380]] },
  ],
  [
    { Réf: 'A-0001', 'Prix de vente': '1200,00', Devise: 'EUR' },
    { Réf: 'A-0002', 'Prix de vente': '850,00', Devise: 'EUR' },
    { Réf: 'A-0003', 'Prix de vente': '2450,50', Devise: 'EUR' },
    { Réf: 'A-0005', 'Prix de vente': '3100,00', Devise: 'EUR' },
  ],
);

// ── Expositions : des dates où jour et mois peuvent s'inverser ───────────

const EXPOSITIONS = table(
  null,
  64,
  [
    {
      nom: 'N°',
      remplissage: 1,
      distinctes: 64,
      format: 'nombre',
      frequentes: [
        ['1', 1],
        ['2', 1],
      ],
    },
    {
      nom: 'Intitulé',
      remplissage: 1,
      distinctes: 62,
      format: 'texte',
      frequentes: [['Hiver blanc', 2]],
    },
    {
      nom: 'Début',
      remplissage: 1,
      distinctes: 51,
      format: 'date_ambigue',
      frequentes: [
        ['03/04/2019', 3],
        ['05/06/2021', 2],
        ['11/02/2022', 2],
      ],
    },
    {
      nom: 'Fin',
      remplissage: 0.97,
      distinctes: 49,
      format: 'date_ambigue',
      frequentes: [
        ['02/05/2019', 3],
        ['07/08/2021', 2],
      ],
    },
    {
      nom: 'Lieu',
      remplissage: 0.9,
      distinctes: 6,
      format: 'texte',
      frequentes: [
        ['Galerie principale', 41],
        ['Salle d’en bas', 12],
      ],
    },
    {
      nom: 'Genre',
      remplissage: 1,
      distinctes: 3,
      format: 'texte',
      frequentes: [
        ['Expo', 48],
        ['Salon', 12],
        ['Hors les murs', 4],
      ],
    },
  ],
  [
    {
      'N°': '1',
      Intitulé: 'Paysages d’été',
      Début: '03/04/2019',
      Fin: '02/05/2019',
      Lieu: 'Galerie principale',
      Genre: 'Expo',
    },
    {
      'N°': '2',
      Intitulé: 'Hiver blanc',
      Début: '11/02/2022',
      Fin: '15/03/2022',
      Lieu: 'Salle d’en bas',
      Genre: 'Expo',
    },
    {
      'N°': '3',
      Intitulé: 'Foire du printemps',
      Début: '05/06/2021',
      Fin: '07/08/2021',
      Lieu: '',
      Genre: 'Salon',
    },
    {
      'N°': '4',
      Intitulé: 'Cabinet de dessins',
      Début: '09/10/2020',
      Fin: '',
      Lieu: 'Galerie principale',
      Genre: 'Expo',
    },
    {
      'N°': '5',
      Intitulé: 'Rétrospective Lenoir',
      Début: '14/09/2018',
      Fin: '20/10/2018',
      Lieu: 'Galerie principale',
      Genre: 'Expo',
    },
  ],
);

// ── Contacts ─────────────────────────────────────────────────────────────

const CONTACTS = table(
  null,
  1204,
  [
    {
      nom: 'Nom complet',
      remplissage: 1,
      distinctes: 1180,
      format: 'texte',
      frequentes: [['Camille Fournier', 2]],
    },
    {
      nom: 'Courriel',
      remplissage: 0.93,
      distinctes: 1100,
      format: 'courriel',
      frequentes: [['camille@exemple.test', 1]],
    },
    {
      nom: 'Téléphone',
      remplissage: 0.61,
      distinctes: 700,
      format: 'texte',
      frequentes: [['01 23 45 67 89', 3]],
    },
    {
      nom: 'Ville',
      remplissage: 0.82,
      distinctes: 90,
      format: 'texte',
      frequentes: [
        ['Paris', 402],
        ['Lyon', 88],
      ],
    },
  ],
  [
    {
      'Nom complet': 'Camille Fournier',
      Courriel: 'camille@exemple.test',
      Téléphone: '01 23 45 67 89',
      Ville: 'Paris',
    },
    {
      'Nom complet': 'Jules Marchand',
      Courriel: 'jules@exemple.test',
      Téléphone: '',
      Ville: 'Lyon',
    },
    {
      'Nom complet': 'Société Nord-Est',
      Courriel: '',
      Téléphone: '03 20 00 00 00',
      Ville: 'Lille',
    },
  ],
);

// ── Contacts : l'export Mailchimp des désabonnés ─────────────────────────

const MAILCHIMP_DESABONNES = table(
  null,
  318,
  [
    {
      nom: 'Email Address',
      remplissage: 1,
      distinctes: 318,
      format: 'courriel',
      frequentes: [['clara.dumas@exemple.test', 1]],
    },
    {
      nom: 'First Name',
      remplissage: 0.86,
      distinctes: 201,
      format: 'texte',
      frequentes: [['Clara', 4]],
    },
    {
      nom: 'Last Name',
      remplissage: 0.86,
      distinctes: 290,
      format: 'texte',
      frequentes: [['Dumas', 3]],
    },
    {
      nom: 'OPTIN_TIME',
      remplissage: 0.92,
      distinctes: 300,
      format: 'date_iso',
      frequentes: [['2021-03-12 09:41:07', 1]],
    },
    {
      nom: 'CONFIRM_TIME',
      remplissage: 0.78,
      distinctes: 244,
      format: 'date_iso',
      frequentes: [['2021-03-12 09:44:51', 1]],
    },
    {
      nom: 'LEID',
      remplissage: 1,
      distinctes: 318,
      format: 'nombre',
      frequentes: [['84120931', 1]],
    },
    {
      nom: 'EUID',
      remplissage: 1,
      distinctes: 318,
      format: 'texte',
      frequentes: [['b3f9c2a17e', 1]],
    },
    {
      nom: 'UNSUB_TIME',
      remplissage: 1,
      distinctes: 311,
      format: 'date_iso',
      frequentes: [['2024-06-03 18:20:44', 2]],
    },
    {
      nom: 'UNSUB_CAMPAIGN_TITLE',
      remplissage: 0.97,
      distinctes: 14,
      format: 'texte',
      frequentes: [['Vernissage de printemps', 96]],
    },
  ],
  [
    {
      'Email Address': 'clara.dumas@exemple.test',
      'First Name': 'Clara',
      'Last Name': 'Dumas',
      OPTIN_TIME: '2021-03-12 09:41:07',
      CONFIRM_TIME: '2021-03-12 09:44:51',
      LEID: '84120931',
      EUID: 'b3f9c2a17e',
      UNSUB_TIME: '2024-06-03 18:20:44',
      UNSUB_CAMPAIGN_TITLE: 'Vernissage de printemps',
    },
    {
      'Email Address': 'm.perrin@exemple.test',
      'First Name': 'Maxime',
      'Last Name': 'Perrin',
      OPTIN_TIME: '2022-11-02 14:03:19',
      CONFIRM_TIME: '',
      LEID: '84120940',
      EUID: '5ad07e91c4',
      UNSUB_TIME: '2024-09-17 07:12:30',
      UNSUB_CAMPAIGN_TITLE: 'Hiver blanc',
    },
    {
      'Email Address': 'atelier.nord@exemple.test',
      'First Name': '',
      'Last Name': '',
      OPTIN_TIME: '',
      CONFIRM_TIME: '',
      LEID: '84120977',
      EUID: '0c42bb18d9',
      UNSUB_TIME: '2025-01-21 11:55:02',
      UNSUB_CAMPAIGN_TITLE: '',
    },
  ],
);

/**
 * Les deux profils Mailchimp se reconnaissent : les en-têtes des désabonnés
 * contiennent toute la signature des abonnés. Le plus précis passe en premier
 * (le profil des désabonnés), à score voisin.
 */
const SUGGESTIONS_MAILCHIMP: SuggestionProfil[] = [
  {
    profilId: ID_PROFIL_MAILCHIMP_DESABONNES,
    version: 1,
    nom: 'Mailchimp (désabonnés)',
    application: 'Mailchimp, export de l’audience : fichier des désabonnés (CSV)',
    nature: 'contacts',
    score: 100,
    trouves: 7,
    attendus: 7,
  },
  {
    profilId: ID_PROFIL_MAILCHIMP_ABONNES,
    version: 1,
    nom: 'Mailchimp (abonnés)',
    application: 'Mailchimp, export de l’audience : fichier des abonnés (CSV)',
    nature: 'contacts',
    score: 83,
    trouves: 5,
    attendus: 6,
  },
];

const GENERIQUE = table(
  null,
  120,
  [
    {
      nom: 'Colonne A',
      remplissage: 1,
      distinctes: 120,
      format: 'texte',
      frequentes: [['Exemple 1', 1]],
    },
    {
      nom: 'Colonne B',
      remplissage: 0.8,
      distinctes: 40,
      format: 'texte',
      frequentes: [['Exemple 2', 5]],
    },
  ],
  [
    { 'Colonne A': 'Exemple 1', 'Colonne B': 'a' },
    { 'Colonne A': 'Exemple 2', 'Colonne B': '' },
  ],
);

const SUGGESTIONS_OEUVRES: SuggestionProfil[] = [
  {
    profilId: 'profil-demo-export-v3',
    version: 2,
    nom: 'Export d’œuvres, logiciel de gestion (v3)',
    application: 'Logiciel de gestion de galerie',
    nature: 'oeuvres',
    score: 92,
    trouves: 9,
    attendus: 10,
  },
  {
    profilId: 'profil-demo-artosera-oeuvres',
    version: 1,
    nom: 'Artosera → Artosera (œuvres)',
    application: 'Artosera',
    nature: 'oeuvres',
    score: 8,
    trouves: 2,
    attendus: 25,
  },
];

/** Le profil d'un fichier du dépôt, d'après son nom. */
export function profilerFichierDemo(
  fichierId: string,
  chemin: string,
  octets: number,
): FichierProfile {
  const nom = chemin.toLowerCase();
  const commun = { fichierId, chemin, octets, sha256: 'a'.repeat(64) };
  if (nom.includes('anciens-stocks'))
    return {
      ...commun,
      erreur: {
        raison: 'illisible',
        message:
          'Le classeur est protégé par un mot de passe : retirez-le, puis redéposez le fichier.',
      },
    };
  const csv = nom.endsWith('.csv');
  const entete = csv
    ? { format: 'csv', encodage: 'windows-1252', separateur: ';' }
    : { format: 'xlsx', encodage: null, separateur: null };
  if (nom.includes('oeuvres'))
    return {
      ...commun,
      ...entete,
      tables: [OEUVRES],
      chemins: [],
      suggestions: SUGGESTIONS_OEUVRES,
    };
  if (nom.includes('prix'))
    return { ...commun, ...entete, tables: [PRIX], chemins: [], suggestions: [] };
  if (nom.includes('exposition'))
    return { ...commun, ...entete, tables: [EXPOSITIONS], chemins: [], suggestions: [] };
  if (nom.includes('mailchimp'))
    return {
      ...commun,
      ...entete,
      // Un export Mailchimp est en UTF-8, séparé par des virgules.
      encodage: 'utf-8',
      separateur: ',',
      tables: [MAILCHIMP_DESABONNES],
      chemins: [],
      suggestions: SUGGESTIONS_MAILCHIMP,
    };
  if (nom.includes('contact'))
    return { ...commun, ...entete, tables: [CONTACTS], chemins: [], suggestions: [] };
  return { ...commun, ...entete, tables: [GENERIQUE], chemins: [], suggestions: [] };
}

/** L'échantillon d'une table, tel que l'aperçu le lit. */
export function lignesDeLEchantillon(tableProfilee: TableProfilee): LigneEchantillon[] {
  return tableProfilee.echantillon.map((ligne) => ({
    ligne: ligne.ligne,
    valeurs: Object.fromEntries(
      Object.entries(ligne.valeurs).map(([cle, valeur]) => [cle, String(valeur ?? '')]),
    ),
  }));
}
